import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { itemService } from "../../api/services/itemService";
import { addToCart } from "../../utils/cartUtils";
import LoginRequiredModal from "../LoginRequiredModal/LoginRequiredModal";
import defaultProductImage from "../../images/Default-Image.jpeg";
import { useCanViewPrices } from "../../utils/pricingVisibility";
import "./ProductShowcase.css";

const ProductShowcase = () => {
  const navigate = useNavigate();
  const showPrices = useCanViewPrices();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  /** Per-product index while hovering (Featured slider) */
  const [featuredSlideById, setFeaturedSlideById] = useState({});
  const featuredHoverIntervalRef = useRef({});

  useEffect(() => {
    const intervals = featuredHoverIntervalRef.current;
    return () => {
      Object.keys(intervals).forEach((id) => {
        clearInterval(intervals[id]);
      });
    };
  }, []);

  // Helper function to get full image URL using API base URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    const baseUrl = "https://wireox.erp.gold";

    // If it's already a full URL, return as is
    if (
      typeof imagePath === "string" &&
      (imagePath.startsWith("http://") || imagePath.startsWith("https://"))
    ) {
      return imagePath;
    }

    // If it's a relative path starting with /, prepend the base URL
    if (typeof imagePath === "string" && imagePath.startsWith("/")) {
      return `${baseUrl}${imagePath}`;
    }

    // If it's a relative path without leading /, prepend base URL with /
    if (typeof imagePath === "string" && imagePath.trim() !== "") {
      return `${baseUrl}/${imagePath}`;
    }

    return "";
  };

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        setLoading(true);
        // Fetch Featured products from API
        const response = await itemService.getItemsWithTagList("Featured");

        // Transform API response to match component structure
        // API response structure: { Pagination: {...}, Items: [...] }
        let fetchedProducts = [];
        const items =
          response?.Items ||
          response?.items ||
          (Array.isArray(response) ? response : []);

        if (items && Array.isArray(items) && items.length > 0) {
          // Filter items where AvailableQty is greater than 0
          const availableItems = items.filter((item) => {
            const availableQty = item.AvailableQty || item.availableQty || 0;
            return availableQty > 0;
          });

          fetchedProducts = availableItems.map((item, index) => {
            const rawImages = Array.isArray(item.Images) ? item.Images : [];
            const imageUrls = rawImages
              .map((img) => {
                const path =
                  typeof img === "object" && img != null ? img.ImagePath : img;
                return path ? getImageUrl(path) : "";
              })
              .filter(Boolean);
            const imageUrl = imageUrls[0] || "";

            // Determine pricing: if DiscountPrice exists and is greater than 0, show it as current price and UnitPrice as original
            // Only show discount if DiscountPrice > 0 AND UnitPrice > DiscountPrice
            const discountPrice =
              item.DiscountPrice && item.DiscountPrice > 0
                ? item.DiscountPrice
                : null;
            const unitPrice =
              item.UnitPrice && item.UnitPrice > 0 ? item.UnitPrice : 0;

            const hasDiscount =
              discountPrice && unitPrice > 0 && unitPrice > discountPrice;
            const currentPrice = hasDiscount ? discountPrice : unitPrice;
            const originalPrice = hasDiscount ? unitPrice : null;

            const primary = imageUrl || defaultProductImage;
            const gallery =
              imageUrls.length > 0 ? imageUrls : [primary];

            return {
              id: item.ItemId || item.id || index + 1,
              name: item.ItemName || item.Name || "Product",
              currentPrice: currentPrice,
              originalPrice: originalPrice,
              hasDiscount: hasDiscount,
              rating: 5,
              reviewCount: 0,
              image: primary,
              images: gallery,
              alt: item.ItemName || item.Name || `Product ${index + 1}`,
            };
          });
        }

        // Limit to 4 products to fill the row
        const maxProducts = 4;
        setProducts(fetchedProducts.slice(0, maxProducts));
      } catch (error) {
        console.error("Error fetching featured products:", error);
        // Set empty array on error
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, []);

  // Handle product click - navigate to product detail page
  const handleProductClick = (itemId) => {
    // Scroll to top before navigation
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(`/product-detail?ItemID=${itemId}`);
  };

  // Handle add to cart
  const handleAddToCart = (product, e) => {
    e.stopPropagation(); // Prevent triggering product click
    addToCart(
      {
        id: product.id,
        name: product.name,
        price: product.currentPrice,
        quantity: 1,
        image: product.images?.[0] || product.image,
      },
      () => setShowLoginModal(true),
    );
  };

  if (loading) {
    return null;
  }

  if (!products || products.length === 0) {
    return null;
  }

  const stopFeaturedSlider = (productId) => {
    const idStr = String(productId);
    if (featuredHoverIntervalRef.current[idStr]) {
      clearInterval(featuredHoverIntervalRef.current[idStr]);
      delete featuredHoverIntervalRef.current[idStr];
    }
    setFeaturedSlideById((prev) => ({ ...prev, [idStr]: 0 }));
  };

  const startFeaturedSlider = (product) => {
    const idStr = String(product.id);
    const gallery = Array.isArray(product.images) ? product.images : [];
    if (gallery.length <= 1) return;

    stopFeaturedSlider(product.id);

    setFeaturedSlideById((prev) => ({ ...prev, [idStr]: 0 }));

    featuredHoverIntervalRef.current[idStr] = setInterval(() => {
      setFeaturedSlideById((prev) => {
        const len = gallery.length;
        const cur = prev[idStr] ?? 0;
        return { ...prev, [idStr]: (cur + 1) % len };
      });
    }, 2200);
  };

  return (
    <div className="ps-product-showcase-section">
      <LoginRequiredModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
      <div className="psu-showcase-container">
        <div className="psu-section-header">
          <div className="psu-accent-block"></div>
          <h2>Featured Products</h2>
        </div>

        <div className="psu-row-container">
          <div className="psu-grid">
            {products.map((product) => {
              const idStr = String(product.id);
              const gallery =
                product.images?.length > 0
                  ? product.images
                  : [product.image || defaultProductImage];
              const safeIdx =
                (featuredSlideById[idStr] ?? 0) % gallery.length;

              return (
              <article key={product.id} className="psu-card">
                <div
                  className="psu-image-wrapper"
                  onClick={() => handleProductClick(product.id)}
                  onMouseEnter={() => startFeaturedSlider(product)}
                  onMouseLeave={() => stopFeaturedSlider(product.id)}
                >
                  <button
                    type="button"
                    className="psu-view-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleProductClick(product.id);
                    }}
                    title="View Product Details"
                    aria-label="View Product Details"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      width="16"
                      height="16"
                    >
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                    </svg>
                  </button>

                  <div className="psu-image-slider-viewport">
                    <div
                      className="psu-image-slider-track"
                      style={{
                        transform: `translateX(-${safeIdx * 100}%)`,
                      }}
                    >
                      {gallery.map((src, i) => (
                        <div
                          key={`${product.id}-slide-${i}`}
                          className="psu-slide-panel"
                        >
                          <img
                            src={src}
                            alt={i === 0 ? product.alt : ""}
                            className={`psu-product-img ${src === defaultProductImage ? "psu-product-img--default" : ""}`}
                            onError={(e) => {
                              const img = e.target;
                              img.onerror = null;
                              img.src = defaultProductImage;
                              img.classList.add("psu-product-img--default");
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  {gallery.length > 1 ? (
                    <div
                      className="psu-slide-dots"
                      aria-hidden="true"
                    >
                      {gallery.map((_, i) => (
                        <span
                          key={i}
                          className={
                            i === safeIdx
                              ? "psu-slide-dot psu-slide-dot--active"
                              : "psu-slide-dot"
                          }
                        />
                      ))}
                    </div>
                  ) : null}

                  <button
                    type="button"
                    className="psu-add-to-cart"
                    onClick={(e) => handleAddToCart(product, e)}
                  >
                    Add To Cart
                  </button>
                </div>

                <div className="psu-details">
                  <h3 onClick={() => handleProductClick(product.id)}>
                    {product.name}
                  </h3>

                  {showPrices && (
                    <div className="psu-price-row">
                      <span className="psu-current-price">
                        ${product.currentPrice}
                      </span>
                      {product.hasDiscount &&
                      product.originalPrice &&
                      product.originalPrice > 0 ? (
                        <span className="psu-old-price">
                          ${product.originalPrice}
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>
              </article>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductShowcase;
